/**
 * GridWidget — recursive grid layout. `contentProps.layout` is the
 * grid coordinates per item; each item's actual content is another
 * widget (recursive case lets you nest grids inside grids per
 * `GridWidget` semantics from mvp-first).
 *
 * The grid math (5px drag threshold, half-cell delta, vertical-only
 * displacement during resize, etc.) lives in frontninja2's grid
 * implementation — ADR-001 through 005 in that repo. This schema only
 * describes the shape; it does NOT validate layout invariants like
 * non-overlapping items (those are runtime concerns of the grid
 * engine, not the data model).
 */
import * as Schema from "effect/Schema";

import { WidgetId } from "../ids.js";

import { EditorWidgetContent } from "./editor.js";
import { FormWidgetContent } from "./form.js";
import { ImageWidgetContent } from "./image.js";
import { TextWidgetContent } from "./text.js";
import { ButtonWidgetContent } from "./button.js";
import { HeadingWidgetContent } from "./heading.js";
import { CardWidgetContent } from "./card.js";
import { InputWidgetContent } from "./input.js";
import { IconWidgetContent } from "./icon.js";
import { InlineGridContent } from "./inlineGrid.js";
import { FormConfig } from "./form-action.js";
import {
  VideoWidgetContent,
  AudioWidgetContent,
  NavWidgetContent,
  SpacerWidgetContent,
} from "./media.js";

export const GridItemLayout = Schema.Struct({
  x: Schema.Int.pipe(Schema.nonNegative()),
  y: Schema.Int.pipe(Schema.nonNegative()),
  w: Schema.Int.pipe(Schema.positive()),
  h: Schema.Int.pipe(Schema.positive()),
});
export type GridItemLayout = Schema.Schema.Type<typeof GridItemLayout>;

/**
 * Widget — the recursive node in the page-content tree. Includes
 * GridWidget itself for nested-grid composition (e.g. a 12-col page
 * grid containing a 3-col grid in one of its cells). Hand-rolled
 * (rather than Schema.Union) because of the recursive reference; we
 * `Schema.suspend()` to defer evaluation.
 */
export const Widget: Schema.Schema<Widget, WidgetEncoded> = Schema.suspend(() =>
  Schema.Struct({
    id: WidgetId,
    content: Schema.Union(
      TextWidgetContent,
      ImageWidgetContent,
      FormWidgetContent,
      EditorWidgetContent,
      ButtonWidgetContent,
      HeadingWidgetContent,
      CardWidgetContent,
      InputWidgetContent,
      IconWidgetContent,
      InlineGridContent,
      VideoWidgetContent,
      AudioWidgetContent,
      NavWidgetContent,
      SpacerWidgetContent,
      GridWidgetContent,
    ),
  }),
);
export interface Widget {
  readonly id: WidgetId;
  readonly content:
    | Schema.Schema.Type<typeof TextWidgetContent>
    | Schema.Schema.Type<typeof ImageWidgetContent>
    | Schema.Schema.Type<typeof FormWidgetContent>
    | Schema.Schema.Type<typeof EditorWidgetContent>
    | Schema.Schema.Type<typeof ButtonWidgetContent>
    | Schema.Schema.Type<typeof HeadingWidgetContent>
    | Schema.Schema.Type<typeof CardWidgetContent>
    | Schema.Schema.Type<typeof InputWidgetContent>
    | Schema.Schema.Type<typeof IconWidgetContent>
    | Schema.Schema.Type<typeof InlineGridContent>
    | Schema.Schema.Type<typeof VideoWidgetContent>
    | Schema.Schema.Type<typeof AudioWidgetContent>
    | Schema.Schema.Type<typeof NavWidgetContent>
    | Schema.Schema.Type<typeof SpacerWidgetContent>
    | GridWidgetContent;
}
export interface WidgetEncoded {
  readonly id: string;
  readonly content:
    | Schema.Schema.Encoded<typeof TextWidgetContent>
    | Schema.Schema.Encoded<typeof ImageWidgetContent>
    | Schema.Schema.Encoded<typeof FormWidgetContent>
    | Schema.Schema.Encoded<typeof EditorWidgetContent>
    | Schema.Schema.Encoded<typeof ButtonWidgetContent>
    | Schema.Schema.Encoded<typeof HeadingWidgetContent>
    | Schema.Schema.Encoded<typeof CardWidgetContent>
    | Schema.Schema.Encoded<typeof InputWidgetContent>
    | Schema.Schema.Encoded<typeof IconWidgetContent>
    | Schema.Schema.Encoded<typeof InlineGridContent>
    | Schema.Schema.Encoded<typeof VideoWidgetContent>
    | Schema.Schema.Encoded<typeof AudioWidgetContent>
    | Schema.Schema.Encoded<typeof NavWidgetContent>
    | Schema.Schema.Encoded<typeof SpacerWidgetContent>
    | GridWidgetContentEncoded;
}

export const GridItem = Schema.Struct({
  layout: GridItemLayout,
  widget: Widget,
});
export type GridItem = Schema.Schema.Type<typeof GridItem>;

/** The editor's grid row height in px when a grid's config sets none: the
 *  canvas draws with it, and publishing writes it (`rowHeight`). One value,
 *  read by both. */
export const EDITOR_ROW_HEIGHT_PX = 60;

export const GridWidgetContent: Schema.Schema<
  GridWidgetContent,
  GridWidgetContentEncoded
> = Schema.suspend(() =>
  Schema.Struct({
    contentType: Schema.Literal("grid"),
    columns: Schema.Int.pipe(Schema.positive()),
    // Per-axis gutters in px (grid 0.8.32 in-canvas gap editing). Optional:
    // absent on pre-gap-editing snapshots — renderers fall back to their
    // historical constant.
    gapX: Schema.optional(Schema.Int.pipe(Schema.nonNegative())),
    gapY: Schema.optional(Schema.Int.pipe(Schema.nonNegative())),
    // The editor's row height in px: published rows are at least this tall
    // (and grow with their content). Absent on snapshots published before it,
    // which render with content-sized rows as they always did.
    rowHeight: Schema.optional(Schema.Int.pipe(Schema.positive())),
    // A section grid that is a form (its inputs submit together). Absent on
    // every other grid.
    form: Schema.optional(FormConfig),
    items: Schema.Array(GridItem),
  }),
);
export interface GridWidgetContent {
  readonly contentType: "grid";
  readonly columns: number;
  readonly gapX?: number | undefined;
  readonly gapY?: number | undefined;
  readonly rowHeight?: number | undefined;
  readonly form?: FormConfig | undefined;
  readonly items: ReadonlyArray<GridItem>;
}
export interface GridWidgetContentEncoded {
  readonly contentType: "grid";
  readonly columns: number;
  readonly gapX?: number | undefined;
  readonly gapY?: number | undefined;
  readonly rowHeight?: number | undefined;
  readonly form?: Schema.Schema.Encoded<typeof FormConfig> | undefined;
  readonly items: ReadonlyArray<{
    readonly layout: Schema.Schema.Encoded<typeof GridItemLayout>;
    readonly widget: WidgetEncoded;
  }>;
}
